import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Clock, UserPlus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import type { ChatConnectionState } from '@/lib/sdk-official/resources/chat'
import { useChat } from './ChatProvider'
import { chatLimitOf } from './chatLimits'

const messageOf = (err: unknown): string | undefined =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message

/** The connection id between the caller and a person, looked up in the pending lists (the rows carry only the state). */
const findRequestId = async (profileId: string, direction: 'incoming' | 'outgoing'): Promise<string | null> => {
  const page = direction === 'incoming'
    ? await sdk.chat.listIncomingRequests({ limit: 50 })
    : await sdk.chat.listOutgoingRequests({ limit: 50 })
  return page.items.find((c) => c.profile?.id === profileId)?.id || null
}

interface ActionsProps {
  profileId: string
  state: ChatConnectionState
  onChange: (next: ChatConnectionState) => void
  size?: 'sm' | 'default'
  className?: string
}

/**
 * F-108 "Precisa adicionar": the connection button for one person.
 * none → Adicionar · pending_out → Pedido enviado (cancel) · pending_in → Aceitar / Recusar.
 * `connected` draws nothing: the caller shows "Conversar".
 */
export const ConnectionActions = ({ profileId, state, onChange, size = 'sm', className }: ActionsProps) => {
  const { t } = useTranslation('chat')
  const chat = useChat()
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  /* 429 requestCooldown: "Adicionar" stays off until the API's Retry-After passes. */
  const [cooldownUntil, setCooldownUntil] = React.useState(0)
  React.useEffect(() => {
    if (!cooldownUntil) return
    const timer = window.setTimeout(() => setCooldownUntil(0), Math.max(0, cooldownUntil - Date.now()))
    return () => window.clearTimeout(timer)
  }, [cooldownUntil])

  const run = async (action: () => Promise<ChatConnectionState>) => {
    setBusy(true)
    setError(null)
    try {
      onChange(await action())
      chat.refreshConversations()
    } catch (err) {
      setError(messageOf(err) || t('errors.generic'))
      /* 429 limits: "Adicionar" stays off until the API's retryAt; with no time given (pending requests) only the message shows. */
      const limit = chatLimitOf(err)
      if (limit?.until) setCooldownUntil(limit.until)
    } finally {
      setBusy(false)
    }
  }

  const add = () => run(async () => (await sdk.chat.requestConnection(profileId)).connection || 'pending_out')
  /* Asking someone who already asked me accepts their request. */
  const accept = () => run(async () => (await sdk.chat.requestConnection(profileId)).connection || 'connected')
  const decline = () => run(async () => {
    const id = await findRequestId(profileId, 'incoming')
    if (id) await sdk.chat.declineConnection(id)
    return 'none'
  })
  const cancel = () => run(async () => {
    const id = await findRequestId(profileId, 'outgoing')
    if (id) await sdk.chat.removeConnection(id)
    return 'none'
  })

  if (state === 'connected') return null

  return (
    <div className={cn('flex flex-col items-end gap-1', className)} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1.5">
        {state === 'none' && (
          <Button size={size} variant="secondary" disabled={busy || cooldownUntil > 0} onClick={add} data-chat-connect>
            <UserPlus className="mr-1 h-3.5 w-3.5" />{t('connections.add')}
          </Button>
        )}
        {state === 'pending_out' && (
          <>
            <span className="inline-flex items-center gap-1 text-xs text-gray-500"><Clock className="h-3.5 w-3.5" />{t('connections.sent')}</span>
            <Button size={size} variant="ghost" className="text-gray-500" disabled={busy} onClick={cancel} aria-label={t('connections.cancel')} title={t('connections.cancel')}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
        {state === 'pending_in' && (
          <>
            <Button size={size} variant="secondary" disabled={busy} onClick={accept} data-chat-accept>
              <Check className="mr-1 h-3.5 w-3.5" />{t('connections.accept')}
            </Button>
            <Button size={size} variant="outline" disabled={busy} onClick={decline}>{t('connections.decline')}</Button>
          </>
        )}
      </div>
      {error && <p className="max-w-[16rem] text-right text-xs text-red-600">{error}</p>}
    </div>
  )
}

/** Undoes an accepted connection with a person (the id comes from the connections list). */
const removeConnectionWith = async (profileId: string): Promise<void> => {
  for (let page = 1; page <= 10; page += 1) {
    const result = await sdk.chat.listConnections({ page, limit: 50 })
    const found = result.items.find((c) => c.profile?.id === profileId)
    if (found) {
      await sdk.chat.removeConnection(found.id)
      return
    }
    const totalPages = result.pagination?.totalPages
    if (!result.items.length || (typeof totalPages === 'number' && page >= totalPages)) return
  }
}

interface RemoveProps {
  profileId: string
  name: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onRemoved: () => void
}

/**
 * "Remover conexão", always confirmed: it says what changes (in "Precisa
 * adicionar" the chat becomes read-only, the full profile closes) and what
 * does not (the conversation stays; asking again waits 24 h).
 */
export const RemoveConnectionDialog = ({ profileId, name, open, onOpenChange, onRemoved }: RemoveProps) => {
  const { t } = useTranslation('chat')
  const chat = useChat()
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      await removeConnectionWith(profileId)
      chat.refreshConversations()
      onRemoved()
      onOpenChange(false)
    } catch (err) {
      setError(messageOf(err) || t('errors.generic'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-md p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t('connections.removeTitle', { name })}</DialogTitle>
          <DialogDescription>{t('connections.removeBody')}</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>{t('confirmBlock.cancel')}</Button>
          <Button variant="destructive" className="w-full sm:w-auto" disabled={busy} onClick={confirm} data-chat-remove-confirm>
            {t('connections.removeConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
